import ChangePassword from 'app/pages/common/profile/components/ChangePassword'
import clsx from 'clsx'
import {
  getTenantCustomDomain,
  isAuthGradientDomain,
  isDa1UniversalLoginHost,
  isSelectiveAiDomains,
} from 'common/HelperFunctions'
import {USER_TYPES} from 'common/constants'
import {calculateErrorMessage, handlePostLoginNavigation} from 'common/helpers'
import {useFormik} from 'formik'
import {useEffect, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link, useLocation} from 'react-router'
import {toast} from 'react-toastify'
import {MainReducer, RootState} from 'setup'
import {REDUX_SAVE_LOGIN_INFO} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'
import {login, verifyLoginCredentials} from 'setup/redux/Actions/SignIn/login'
import {getLoggedInUserData} from 'setup/redux/Actions/SignIn/login'
import * as Yup from 'yup'

import {AiLoginErrorAlert} from '../../components/AiLoginErrorAlert'
import {SelectiveAiGoogleLogin} from '../../components/SelectiveAiGoogleLogin'
import {useAiLoginError} from '../../hooks/useAiLoginError'
import {useUniversalLogin} from '../../hooks/useUniversalLogin'
import {armLoginTimeout} from '../../loginTimeout'
import MultiFactorAuth from '../../modals/MultiFactorAuth'
import {UniversalLoginSchoolPicker} from '../../modals/UniversalLoginSchoolPicker'

const initialValues = {
  email: '',
  password: '',
}

const userDetailInitialValue = {
  userType: '',
  userEmail: '',
  userId: '',
  userPassword: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

/**
 * Explicitly hand the exact credentials the user just typed to the browser's
 * password manager. This overrides any similar-but-different email that may be
 * sitting in autofill/cache, so the "Save password?" prompt always offers the
 * email + password that were actually entered.
 *
 * Chrome/Edge support the Credential Management API (PasswordCredential) and get
 * a hard guarantee. Safari ignores it and relies on the form's autocomplete
 * attributes (email = "email", password = "current-password") to save the same.
 */
const saveEnteredCredentials = (email: string, password: string) => {
  try {
    const w = window as any
    if (typeof w.PasswordCredential === 'function' && navigator.credentials?.store) {
      const credential = new w.PasswordCredential({
        id: email,
        password,
        name: email,
      })
      navigator.credentials.store(credential).catch(() => {})
    }
  } catch {
    // Browser doesn't support the Credential Management API — the form's
    // autocomplete attributes handle saving instead. No action needed.
  }
}

export function Login() {
  const dispatch = useDispatch()
  const location = useLocation()

  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings,
    shallowEqual
  ) as MainReducer['generalSettings']

  const isSelectiveAiDomain = isSelectiveAiDomains()
  const emailLabel = isSelectiveAiDomain ? 'Email' : 'Email/Username'
  const emailPlaceholder = isSelectiveAiDomain ? 'Email' : 'Email/Username'

  const loginSchema = Yup.object().shape({
    email: isSelectiveAiDomain
      ? Yup.string()
          .email('Enter a valid email address')
          .min(3)
          .max(50)
          .required('Email is required')
          .label(emailLabel)
      : Yup.string().min(3).max(50).required('Email/Username is required').label(emailLabel),
    password: Yup.string()
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Password is required'),
  })

  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showMfaModal, setShowMfaModal] = useState(false)
  const [userDetails, setUserDetails] = useState(userDetailInitialValue)
  const [changePassword, setChangePassword] = useState<any>(null)

  const {
    isUniversalLoginHost,
    showSchoolPicker,
    schoolInstances,
    pickerLoading,
    aiLoginError: universalAiLoginError,
    emailLoginOnly: universalEmailLoginOnly,
    showEmailLoginForm: universalShowEmailLoginForm,
    submitUniversalLogin,
    handleSchoolSelect,
    closeSchoolPicker,
  } = useUniversalLogin()

  const {
    aiLoginError: tenantAiLoginError,
    emailLoginOnly: tenantEmailLoginOnly,
    showEmailLoginForm: tenantShowEmailLoginForm,
    processAiLoginError,
  } = useAiLoginError()

  const aiLoginError = universalAiLoginError ?? tenantAiLoginError
  const emailLoginOnly = universalEmailLoginOnly || tenantEmailLoginOnly

  function onModalError(message: string) {
    setShowMfaModal(false)
    toast.error(message)
  }

  const proceedLogin = (
    email: string,
    password: string,
    setSubmitting?: (isSubmitting: boolean) => void,
    setStatus?: (status: string) => void
  ) => {
    const cancelTimeout = armLoginTimeout({setLoading, setSubmitting, setStatus})
    dispatch(
      login(
        email,
        password,
        (res: any) => {
          cancelTimeout()
          const {promptChangePassword} = res.result

          if (promptChangePassword) {
            setChangePassword(res)
            return
          }

          if (isSelectiveAiDomains()) {
            // Save exactly the email + password that were entered (strict), not a
            // similar value that may be in the browser's autofill/cache.
            saveEnteredCredentials(email, password)
            dispatch(
              getLoggedInUserData(
                () => {},
                () => {}
              )
            )
          }

          setLoading(false)
          if (setSubmitting) setSubmitting(false)

          if (
            handlePostLoginNavigation(res.result, {
              path: '/dashboard',
              searchParams: {step: '0'},
            })
          ) {
            return
          }
        },
        (res: any) => {
          cancelTimeout()
          if (isSelectiveAiDomains()) {
            const outcome = processAiLoginError(
              res,
              (message) => setStatus?.(message),
              'Invalid username or password'
            )
            if (outcome !== 'default') {
              setLoading(false)
              if (setSubmitting) setSubmitting(false)
              return
            }
          }
          const message = res?.message ?? 'Invalid username or password'
          if (setStatus) setStatus(message)
          setLoading(false)
          if (setSubmitting) setSubmitting(false)
        }
      )
    )
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setSubmitting(true)
      setStatus('')

      const onFailure = (err: any) => {
        setStatus(calculateErrorMessage(err, 'Invalid username or password'))
        setLoading(false)
        setSubmitting(false)
      }

      if (isUniversalLoginHost && !isDa1UniversalLoginHost()) {
        submitUniversalLogin(values.email, values.password, {
          setStatus,
          setSubmitting,
          onLoadingChange: setLoading,
        })
        return
      }

      setTimeout(() => {
        const cancelTimeout = armLoginTimeout({setLoading, setSubmitting, setStatus})
        dispatch(
          verifyLoginCredentials(
            values.email,
            values.password,
            (res: any) => {
              cancelTimeout()
              const {userType, enforceMfa} = res.result

              if (enforceMfa && [USER_TYPES.ADMIN].includes(userType)) {
                setUserDetails({...res.result, userPassword: values.password})
                setShowMfaModal(true)
                setLoading(false)
                setSubmitting(false)
              } else {
                proceedLogin(values.email, values.password, setSubmitting, setStatus)
              }
            },
            (err: any) => {
              cancelTimeout()
              onFailure(err)
            }
          )
        )
      }, 1000)
    },
  })

  const handleSignInWithEmail = (email?: string) => {
    if (universalAiLoginError) {
      const prefilled = universalShowEmailLoginForm(email)
      if (prefilled) formik.setFieldValue('email', prefilled)
      return
    }
    const prefilled = tenantShowEmailLoginForm(email)
    if (prefilled) formik.setFieldValue('email', prefilled)
  }

  useEffect(() => {
    const state = location.state as {emailLoginOnly?: boolean; email?: string} | null
    if (state?.emailLoginOnly) {
      const prefilled = tenantShowEmailLoginForm(state.email)
      if (prefilled) {
        formik.setFieldValue('email', prefilled)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  if (showMfaModal) {
    return (
      <MultiFactorAuth
        showModal={showMfaModal}
        closeModal={() => setShowMfaModal(false)}
        userDetails={userDetails}
        onModalError={onModalError}
        proceedLogin={proceedLogin}
        setSubmitting={formik?.setSubmitting}
        setStatus={formik?.setStatus}
      />
    )
  }

  if (changePassword) {
    return (
      <ChangePassword
        username={changePassword?.result?.user?.email || formik.values.email}
        callback={() => {
          const payload = JSON.parse(JSON.stringify(changePassword))
          payload.result.promptChangePassword = false
          dispatch({
            type: REDUX_SAVE_LOGIN_INFO,
            payload,
          })
          setChangePassword(null)
          setLoading(false)
          handlePostLoginNavigation(payload.result, {
            path: '/dashboard',
            searchParams: {step: '0'},
          })
        }}
      />
    )
  }

  const isSpecialDomain = isAuthGradientDomain()
  const authGradientBtnStyle = {
    background: 'linear-gradient(90deg, #c41e3a 0%, #1e5a8a 100%)',
    border: 'none',
    color: 'white',
  }
  const authDarkInputStyle = isSpecialDomain
    ? {
        backgroundColor: 'rgba(60, 60, 70, 0.9)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        color: 'white',
      }
    : undefined

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-11'>
          <h1 className={clsx('fw-bolder mb-3', isSpecialDomain ? 'text-white' : 'text-gray-900')}>
            {isSpecialDomain ? 'Sign in' : 'Sign In'}
            {!isSpecialDomain && generalSettings?.tenantName && (
              <> to {generalSettings.tenantName}</>
            )}
          </h1>
          {isSpecialDomain && (
            <div className='fw-semibold fs-6 text-white opacity-75'>
              Sign in to{' '}
              <a
                href='https://da1timetable.ai'
                target='_blank'
                rel='noreferrer'
                className='text-primary text-decoration-underline'
              >
                Da1Timetable
              </a>
            </div>
          )}
        </div>
        {/* begin::Heading */}

        {aiLoginError ? (
          <AiLoginErrorAlert
            error={aiLoginError}
            isSpecialDomain={isSpecialDomain}
            onSignInWithEmail={handleSignInWithEmail}
          />
        ) : formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : null}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label
            className={clsx(
              'form-label fs-6 fw-bolder',
              isSpecialDomain ? 'text-white' : 'text-gray-900'
            )}
          >
            {emailLabel} <span style={{color: '#ef4444'}}>*</span>
          </label>
          <input
            placeholder={emailPlaceholder}
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control',
              !isSpecialDomain && 'bg-transparent',
              formik.touched.email && formik.errors.email && 'is-invalid'
            )}
            style={authDarkInputStyle}
            type='email'
            name='email'
            id='email'
            autoComplete='username'
            autoFocus
          />
          {formik.touched.email && formik.errors.email && (
            <div className='invalid-feedback'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <div className='d-flex flex-stack mb-2'>
            <label
              className={clsx(
                'form-label fw-bolder fs-6 mb-0',
                isSpecialDomain ? 'text-white' : 'text-gray-900'
              )}
            >
              Password <span style={{color: '#ef4444'}}>*</span>
            </label>
            <Link
              to='/auth/forgot-password'
              className={clsx('link-primary', isSpecialDomain && 'text-decoration-underline')}
            >
              {isSelectiveAiDomain ? 'Forgot / Set Password?' : 'Forget Password?'}
            </Link>
          </div>
          <div className='position-relative'>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete='current-password'
              id='current-password'
              placeholder='Password'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control',
                !isSpecialDomain && 'bg-transparent',
                formik.touched.password && formik.errors.password && 'is-invalid'
              )}
              style={{...authDarkInputStyle, paddingRight: '4.5rem'}}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='invalid-feedback'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            )}

            <span
              className={clsx(
                'position-absolute end-0 top-50 translate-middle-y',
                formik.touched.password && formik.errors.password ? 'me-12' : 'me-5',
                isSpecialDomain && 'text-white'
              )}
            >
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='tooltip-show-hide-password'>
                    {showPassword ? 'Hide' : 'Show'} Password
                  </Tooltip>
                }
              >
                <i
                  className={clsx('cursor-pointer fa', {
                    'fa-eye': !showPassword,
                    'fa-eye-slash': showPassword,
                  })}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={3}
                  aria-hidden='true'
                  aria-label='Show password'
                  role='button'
                />
              </OverlayTrigger>
            </span>
          </div>
        </div>
        {/* end::Form group */}

        {/* begin::Action */}
        <div className='d-grid mb-10'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className={clsx('btn rounded', !isSpecialDomain && 'btn-primary')}
            style={isSpecialDomain ? authGradientBtnStyle : undefined}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              !formik.values.email?.trim() ||
              !formik.values.password
            }
          >
            {!loading && (
              <span className='indicator-label'>{isSelectiveAiDomain ? 'Login' : 'Continue'}</span>
            )}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>

        {generalSettings.admission_form_permission === 1 && (
          <>
            <div className='separator separator-content my-14'>
              <span className='w-125px text-gray-500 fw-semibold fs-7'>Or Apply For</span>
            </div>

            <div className='d-grid'>
              <a
                id='kt_admission_form_redirect'
                href={
                  generalSettings.new_admission_form === 1
                    ? `${import.meta.env.BASE_URL}admissions`
                    : `https://${getTenantCustomDomain()}/admission/admissionForm`
                }
                target='BLANK'
                className='btn btn-outline btn-outline-dashed btn-outline-danger btn-active-light-danger pulse pulse-danger w-100'
              >
                {!loading && <span className='indicator-label'>Admission</span>}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </a>
            </div>
          </>
        )}
        {/* end::Action */}

        {/* begin::Google Login */}
        {!emailLoginOnly ? (
          <SelectiveAiGoogleLogin disabled={loading} isSpecialDomain={isSpecialDomain} />
        ) : null}
        {/* end::Google Login */}
      </form>
      <UniversalLoginSchoolPicker
        show={showSchoolPicker}
        instances={schoolInstances}
        loading={pickerLoading}
        onSelect={(instance) =>
          handleSchoolSelect(instance, {
            setStatus: formik.setStatus,
            setSubmitting: formik.setSubmitting,
            onLoadingChange: setLoading,
          })
        }
        onClose={closeSchoolPicker}
      />
    </>
  )
}
